import { API_BASE } from '../lib/api-base';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SymphonyPricing } from '../components/SymphonyPricing';
import { ScrollArea } from '../components/symphony/ui';
import { DesktopBack } from '../components/symphony/Breadcrumb';
import { EmbeddedCheckout, getStripe } from '../lib/stripe/embedded';
import {
  ELEMENTS_LANE_PARAM,
  clearStashedElementsReturn,
  type ElementsReturn,
  readElementsReturn,
  readStashedElementsReturn,
  resolveElementsReturn,
  stashElementsReturn,
} from '../lib/elements-return';
import { Cadence } from '../lib/plans';
import { useBasePath } from '../lib/use-base-path';
import { checkoutHref } from '../lib/checkout-route';
import { usePersonalSaleActive } from '../lib/use-personal-sale';
import { PERSONAL_SALE_COUPON, shouldAttemptPersonalSaleCoupon } from '../lib/personal-sale';
import { useSuppressPersonalSale } from '../lib/personal-sale-suppression';
import { validateCouponCode } from '../lib/coupon';
import { HOSTED_FALLBACK_PARAM } from '../lib/checkout-fallback';
import { useTranslation } from '../lib/i18n';
import { CREDITS_REFRESH_EVENT } from '../lib/use-credits';
import { logPackagePickerView, logPurchasePageView, logPurchaseSuccessOnce } from '../lib/appsflyer';
import { registerGa4CheckoutSession } from '../lib/firebase-analytics';
import { GtmEvent, pushGtmEvent } from '../lib/gtm';
import { reportPurchaseSuccess } from '../lib/purchase-success';
import { useGenericBi } from '../lib/use-generic-bi';
import { usePageLoadBi } from '../lib/use-page-load-bi';
import { useAffiliateLandingRedirect } from '../lib/use-affiliate-redirects';
import { useLoadBi } from '../lib/use-load-bi';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { readUpgradeAttribution } from '../lib/upgrade-origin';
import { stripParams } from '../lib/strip-url-params';
import {
  clearPurchaseAttribution,
  purchaseVerifiedFields,
  readPurchaseAttribution,
  stashPurchaseAttribution,
  type PurchaseAttribution,
} from '../lib/purchase-attribution';
import { PurchaseAttributionContext } from '../lib/purchase-attribution-context';

// How long a just-created Checkout Session is re-opened instead of minting a new one. Stripe keeps
// a session valid for ~24h; 2 minutes is well inside that and covers the observed burst (8 clicks
// in 3 minutes) while still letting a buyer start a genuinely fresh attempt later.
const CHECKOUT_REUSE_MS = 120_000;
// How hard to try to settle a redirect return before leaving it to the premium poll. A handful of
// attempts a second apart covers a transient network miss without keeping the buyer waiting.
const RETURN_VERIFY_ATTEMPTS = 4;
const RETURN_VERIFY_RETRY_MS = 1_000;
// How long the CTA may stay in its busy state waiting for Stripe's form to mount before it is
// released so the buyer can retry. Generous enough for a slow mobile connection, short enough that
// a failed mount does not look like a hung page.
const CHECKOUT_BUSY_TIMEOUT_MS = 20_000;

// Mount prefix Pepyaka strips in prod; mirrors the other orion-web lib files.
// All three mounts (/orion-web, /symphony-web, /symphony) route their /api/*
// through the same handler, so a single prefix works for either.

// Legacy Wix ("Wixel") pricing page — the fallback package picker shown to a visitor the Symphony
// picker is not (yet) available to (non-US, non-employee; see lib/eligibility.ts). Same destination
// the mobile app opens (features/core/services/upgrade-credits) and the Settings upgrade link uses;
// purchases there reflect in the user's account. Embedded in an iframe rather than redirected to, so
// the visitor stays in-app. Framing is permitted: the page's CSP frame-ancestors allows *.wix.com,
// which covers every mount (symphony.wix.com and manage.wix.com).
const WIXEL_PRICING_URL =
  'https://manage.wix.com/wixel-pricing/select-offer?cycleId=1-YEAR&referralAdditionalInfo=ai_credits_orion';

// The backdrop behind either checkout modal. Matches the shared Modal's scrim (light dim + blur);
// `.orion-pane-modal` already scopes it to the content pane, so the sidebar stays crisp and only the
// plans behind the checkout blur.
//
// ONE declaration for both lanes, deliberately. A scrim is dark in both themes — a mode-independent
// colour the design-token check allows by exception — and an exception is only defensible while
// there is a single copy of it to point at.
const CHECKOUT_SCRIM: CSSProperties = {
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '40px 16px',
  background: 'rgba(var(--ds-black-rgb), 0.28)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
};

// Stash of the plan a buyer just sent to Stripe Checkout, so the return page can optimistically show
// the subscribed view for it the instant the payment is confirmed `paid` — before the async webhook
// grants the real subscription. sessionStorage (not React state) because Stripe's return redirect is a
// full navigation that resets in-memory state. Consumed once, on the paid return.
const PENDING_PURCHASE_KEY = 'orion.pendingPurchase';

/** What the Elements checkout screen is open for. Exactly one of the two ids, per the target rule. */
function readPendingPurchase(): { agentPlanId: string; cadence: Cadence } | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { agentPlanId?: unknown; cadence?: unknown };
    if (typeof parsed.agentPlanId !== 'string') return null;
    return { agentPlanId: parsed.agentPlanId, cadence: parsed.cadence === 'yearly' ? 'yearly' : 'monthly' };
  } catch {
    return null;
  }
}

function clearPendingPurchase() {
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    /* sessionStorage unavailable — nothing to clear */
  }
}

// Verifying a Checkout session can transiently fail (a network blip, a cold Lambda) — retry a
// few times before giving up, so a real payment doesn't lose its AppsFlyer/GA4 purchase event to
// a single flaky fetch. A fixed delay schedule, same style as the credits daily-reset poll
// (use-credits.ts's DAILY_RESET_RETRY_DELAYS_MS) rather than a new backoff formula.
const CHECKOUT_VERIFY_RETRY_DELAYS_MS = [0, 2_000, 5_000];

// Persisted (localStorage — survives a closed tab, unlike PENDING_PURCHASE_KEY's sessionStorage)
// record of a Checkout session whose payment has not yet been CONFIRMED. Written before the
// verify fetch starts and before the URL is stripped, so a tab closed or reloaded mid-verification
// doesn't lose the only reference that would let a later visit finish reporting the purchase.
// Cleared as soon as verification resolves, either way (paid or genuinely not paid) — left in
// place only while the session's fate is still unknown.
const PENDING_VERIFY_KEY = 'symphony-checkout-verify-pending';

function readPendingVerify(): { sessionId: string; billingLane?: string } | null {
  try {
    const raw = window.localStorage.getItem(PENDING_VERIFY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { sessionId?: unknown; billingLane?: unknown };
    if (typeof parsed.sessionId !== 'string') return null;
    return {
      sessionId: parsed.sessionId,
      billingLane: typeof parsed.billingLane === 'string' ? parsed.billingLane : undefined,
    };
  } catch {
    return null;
  }
}

function writePendingVerify(sessionId: string, billingLane?: string) {
  try {
    window.localStorage.setItem(PENDING_VERIFY_KEY, JSON.stringify({ sessionId, billingLane }));
  } catch {
    /* localStorage unavailable — verification just can't resume across a lost tab */
  }
}

function clearPendingVerify() {
  try {
    window.localStorage.removeItem(PENDING_VERIFY_KEY);
  } catch {
    /* nothing to clear */
  }
}

type CheckoutVerifyResult =
  | { outcome: 'paid'; amount: number | null; currency: string | null }
  | { outcome: 'not_paid' }
  | { outcome: 'unresolved' };

// Fetch session-status with retries, report the purchase (fire-once, per session id — see
// logPurchaseSuccessOnce) on a confirmed paid outcome, and only clear the persisted pending-verify
// record once the session's fate is actually known. Exhausting every retry leaves the record in
// place so the NEXT mount of this page — this tab reopened, or a later visit — picks it back up.
async function verifyCheckoutSession(
  sessionId: string,
  billingLane: string | undefined,
  // Called only when THIS call reported the purchase — i.e. inside `logPurchaseSuccessOnce`'s
  // per-session latch — so `purchase_verified` fires at most once per session, like `af_purchase`.
  onFirstReport: (attribution: PurchaseAttribution, amount: number, currency: string) => void,
): Promise<CheckoutVerifyResult> {
  // What went wrong on the LAST attempt, kept purely for diagnostics — this is what tells a real
  // test run whether the break is "the endpoint 401'd" (session-status.ts requires a wixSession2
  // cookie), a 5xx, a network error, or a genuine non-paid session, instead of a bare "incomplete".
  let lastFailure: { reason: 'http_error' | 'network_error'; status?: number } | null = null;
  for (let attempt = 0; attempt < CHECKOUT_VERIFY_RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, CHECKOUT_VERIFY_RETRY_DELAYS_MS[attempt]));
    try {
      const res = await fetch(
        `${API_BASE}/api/stripe/session-status?session_id=${encodeURIComponent(sessionId)}${
          billingLane === 'sandbox' ? '&billing_lane=sandbox' : ''
        }`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        lastFailure = { reason: 'http_error', status: res.status };
        console.warn(`[packages] session-status attempt ${attempt + 1} failed with HTTP ${res.status}`, {
          sessionId,
        });
        continue;
      }
      const s = (await res.json()) as {
        status?: string;
        paymentStatus?: string;
        amount?: number | null;
        currency?: string | null;
      };
      const paid = s.status === 'complete' && (s.paymentStatus === 'paid' || s.paymentStatus === 'no_payment_required');
      clearPendingVerify();
      if (!paid) {
        console.warn('[packages] session verified but not paid — this is the real Stripe answer, not a bug', {
          sessionId,
          status: s.status,
          paymentStatus: s.paymentStatus,
        });
        return { outcome: 'not_paid' };
      }
      const amount = typeof s.amount === 'number' ? s.amount : null;
      const currency = s.currency || null;
      // AppsFlyer uses its own af_revenue/af_currency (inside logPurchaseSuccessOnce); GA4/GTM's
      // purchase tag reads the canonical value/currency the caller pushes from the result below.
      // What sent the buyer, stashed when this checkout started (the return is a fresh page load that
      // no longer carries the picker's query). Read, reported, and CLEARED — so a later purchase in this
      // tab cannot inherit it.
      const attribution = readPurchaseAttribution();
      const reported =
        amount !== null && currency ? logPurchaseSuccessOnce(sessionId, amount, currency, attribution.origin) : false;
      if (reported && amount !== null && currency) {
        onFirstReport(attribution, amount, currency);
      }
      clearPurchaseAttribution();
      console.info('[packages] purchase verified paid', { sessionId, amount, currency, reported });
      return { outcome: 'paid', amount, currency };
    } catch (err) {
      lastFailure = { reason: 'network_error' };
      console.warn(`[packages] session-status attempt ${attempt + 1} threw`, { sessionId, err });
    }
  }
  console.error('[packages] could not verify Stripe session status after retries — will resume on next visit', {
    sessionId,
    lastFailure,
  });
  return { outcome: 'unresolved' };
}

// Drop the Stripe-return query params (?status, ?session_id) from the address bar without a
// navigation, so a manual refresh loads a clean URL and can't replay the checkout-return state.
// history.replaceState (not next/router) is deliberate: it leaves router.query untouched, so the
// in-flight verify/optimistic flow keyed on those values isn't torn down mid-run.
// Stripe's own return parameters, which only the Elements lane comes back with. Stripped for the
// same reason as ours — a refresh must not replay the outcome — and named here rather than inline
// because a missed one leaves a client secret sitting in the address bar and in browser history.
const STRIPE_RETURN_PARAMS = [
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
];

function stripCheckoutParams() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const ours = ['status', 'session_id', 'billing_lane', ELEMENTS_LANE_PARAM];
  const all = [...ours, ...STRIPE_RETURN_PARAMS];
  if (!all.some((param) => url.searchParams.has(param))) return;
  stripParams(all);
}

// Package picker. Every mount now renders the Symphony pricing design
// (SymphonyPricing, fed by the AgentPlan catalog) — the legacy Orion POC layout
// was retired. Buying opens Stripe **embedded** Checkout in a modal (stays on
// our domain); on completion Stripe returns to ?status=success and the picker
// polls the caller's premium so the new subscription shows up on its own.

export default function PackagesPage() {
  const router = useRouter();
  const basePath = useBasePath();
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  // WHAT SENT THEM HERE. Read once, synchronously, off window.location — `router.query` is empty on the
  // first client render and the view events below fire on mount, so reading it from the router would
  // attribute every view to nothing. `conversationId` is set only by the assistant's links (the halt
  // wall and the model-emitted skill link), and joins this view to the impression that produced it.
  //
  // Without this the picker has one undifferentiated exposure number: when it moves, nothing says which
  // surface moved it, and a prompt change, a new upsell card and a real product change look identical.
  const [attribution] = useState(readUpgradeAttribution);
  // The `purchase_verified` 198:10 (docs/compliance/bi.md §2) — the purchase step the funnel joins
  // `buy_plan` to, carrying the origin/feature stashed at checkout start. Every caller holds a once-only
  // latch (the hosted lane's per-session `logPurchaseSuccessOnce`, the Elements lane's stash clear).
  const logPurchaseVerified = (purchase: PurchaseAttribution, amount: number | null, currency: string | null) => {
    logPageClickBi(
      { page_name: 'packages', button_name: 'purchase_verified' },
      { additionalFields: purchaseVerifiedFields(purchase, amount, currency) },
    );
  };
  const logPurchaseVerifiedRef = useRef(logPurchaseVerified);
  logPurchaseVerifiedRef.current = logPurchaseVerified;
  // `origin` is a registered src:172 field; `conversation_id` is not on the page-LOAD event (only on the
  // click), so it rides the generic page-view's additionalFields. That is the join key that turns
  // "someone came from the credit wall" into "THIS wall impression produced this view".
  usePageLoadBi(
    { page_name: 'packages', origin: attribution.origin },
    attribution.conversationId ? { additionalFields: { conversation_id: attribution.conversationId } } : true,
  );
  const [pendingId, setPendingId] = useState<string | null>(null);

  // A checkout POST already in flight. `pendingId` cannot guard this on its own: setState is
  // asynchronous and batched, so two clicks landing before the next render both read the old
  // value and both mint a Stripe Checkout Session. A ref is written synchronously, so the second
  // click sees it immediately.
  const checkoutInFlightRef = useRef(false);
  // The last session we successfully created, so repeat clicks on the SAME target re-open that
  // session instead of minting another one.
  //
  // This is not cosmetic tidying. On 2026-08-20 a single buyer produced EIGHT identical
  // `Symphony Basic month $20` sessions in three minutes — some 3 seconds apart — and not one of
  // them reached a payment attempt: the signature of someone clicking again because the modal did
  // not visibly open. Repeat sessions doubled that day (43 vs the usual ~20) and dragged the
  // measured checkout conversion rate from ~34% to ~20% on their own, which is a real number
  // people make decisions from. Every extra click also mints a live Stripe object we never use.
  //
  // Deliberately NOT gated on the modal being open: if the modal failed to render, the buyer must
  // still be able to try again. Re-opening the same session is the safe way to allow that — a
  // Stripe Checkout Session stays valid for ~24h, so reuse costs nothing and the buyer lands
  // exactly where they left off.
  const lastCheckoutRef = useRef<{ key: string; at: number; clientSecret: string; publishableKey: string | null } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // Whether this visitor may use the picker (US-only for now, Wix employees exempt). null = still
  // resolving (don't flash the plans); false = a real region block (server said allowed:false);
  // 'error' = the eligibility probe itself failed (infra/network) — show a retry state, NOT the
  // misleading "not available in your region" copy, since an eligible US user could land here on a
  // transient blip. The server enforces the same gate on plans/checkout/switch — this only picks UX.
  const [eligible, setEligible] = useState<boolean | 'error' | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [embeddedSecret, setEmbeddedSecret] = useState<string | null>(null);
  // Whether this buyer pays on a screen WE draw (Stripe Elements) or inside Stripe's hosted
  // Checkout iframe. Resolved server-side per billing lane — see lib/elements-checkout — because the
  // two lanes create different Stripe objects and a page that guessed would build a form against a
  // lane the server then refuses. Null until the config read lands; nothing branches on it before
  // then, so a slow read simply delays the first Buy click's destination rather than defaulting.
  const [elementsCheckout, setElementsCheckout] = useState<boolean | null>(null);
  // The billing lane, read from the same config as `elementsCheckout`. Gates the auto personal-sale
  // coupon out of the sandbox lane, where a coupon is refused. Defaults false ⇒ live (the lane that
  // has always worked), so an unreadable config never suppresses the discount for a live buyer.
  const [sandbox, setSandbox] = useState<boolean>(false);
  const personalSaleActive = usePersonalSaleActive();
  // The purchase the Elements screen is open for; null when it is closed. It carries everything the
  // screen needs to describe the order, because that copy lives in the picker, not here.
  const [checkoutReady, setCheckoutReady] = useState(false);
  // `started` is a purchase that completed with NO money moving — a free first period, where the
  // buyer stored a card and the subscription is still being created by the setup webhook. It is a
  // success, and it is not a payment; conflating the two toasts "Payment received" for a charge
  // that never happened.
  const [paymentOutcome, setPaymentOutcome] =
    useState<'verifying' | 'paid' | 'started' | 'incomplete' | null>(null);
  // The plan a just-confirmed purchase bought (recovered from sessionStorage on a paid return),
  // handed to the picker so it flips to the subscribed view immediately, before the webhook grant.
  const [optimisticPurchase, setOptimisticPurchase] = useState<{ agentPlanId: string; cadence: Cadence } | null>(null);
  const checkoutRef = useRef<EmbeddedCheckout | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);

  const status = typeof router.query.status === 'string' ? router.query.status : undefined;

  // Which payment form this buyer gets. Read once, from the server, for the reason on the state
  // declaration above.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/config`);
        if (!res.ok || cancelled) return;
        const body = (await res.json()) as { elementsCheckout?: boolean; sandbox?: boolean };
        setElementsCheckout(Boolean(body.elementsCheckout));
        setSandbox(Boolean(body.sandbox));
      } catch {
        // Unreadable config leaves it null, which means hosted Checkout — the lane that has always
        // worked. Failing toward the new screen would be the wrong way round.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // A deeplinked top-up request (?topUpPackageId=…) — the mobile apps open the picker with it to
  // jump straight into the current plan's top-up checkout. Captured once into state and stripped
  // from the address bar immediately (same history.replaceState treatment as the Stripe-return
  // params) so a refresh/back — or the Stripe return redirect — can't replay the auto-open. Read
  // off window.location, not router.query, so it's available on first client render without
  // waiting for router hydration. The login bounce preserves it: redirectToLogin sends the full
  // URL as postLogin, so an unauthenticated open lands back here with the param intact.
  const [autoTopupPackId, setAutoTopupPackId] = useState<string | null>(null);
  useEffect(() => {
    const url = new URL(window.location.href);
    const packId = url.searchParams.get('topUpPackageId');
    if (!packId) return;
    stripParams(['topUpPackageId']);
    setAutoTopupPackId(packId);
  }, []);

  // The Elements lane went off while a buyer had the checkout page open, and that page sent them
  // back here to be served by hosted Checkout instead. Same shape as the top-up deeplink above:
  // read once, strip from the address bar so a refresh or a Back cannot replay a checkout the buyer
  // may have already finished, and degrade to the plain picker when the target does not resolve.
  const hostedFallbackFired = useRef(false);
  useEffect(() => {
    if (hostedFallbackFired.current) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get(HOSTED_FALLBACK_PARAM) !== '1') return;
    hostedFallbackFired.current = true;
    const agentPlanId = url.searchParams.get('agentPlanId') ?? undefined;
    const packId = url.searchParams.get('packId') ?? undefined;
    const cadence: Cadence = url.searchParams.get('cadence') === 'yearly' ? 'yearly' : 'monthly';
    const couponCode = url.searchParams.get('couponCode') ?? undefined;
    stripParams([HOSTED_FALLBACK_PARAM, 'agentPlanId', 'packId', 'cadence', 'couponCode']);
    if (packId) {
      void startCheckout({ packId }, packId);
      return;
    }
    if (agentPlanId) {
      void startCheckout(
        { agentPlanId, cadence, couponCode },
        agentPlanId,
        `${agentPlanId}:${cadence}:${couponCode ?? ''}`,
      );
    }
    // startCheckout is stable for this purpose and re-running would re-open a checkout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop the attribution params from the address bar once they are captured, the same treatment every
  // other param on this page gets (topUpPackageId above, the Stripe-return trio in stripCheckoutParams).
  // A refresh is not a new hand-off: leaving `origin` in the URL would report a second and third view
  // from one credit wall, and `conversation_id` would keep joining them to the same impression. Safe to
  // do in an effect because `attribution` was read during render, before this runs. history.replaceState,
  // not the router, so nothing keyed on router.query is torn down.
  useEffect(() => {
    if (!attribution.origin && !attribution.conversationId && !attribution.feature) return;
    stripParams(['origin', 'conversation_id', 'feature']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The eligibility probe gates whether the plans list renders, so track it as the
  // primary plans-list load region (null = in flight, 'error' = probe failed).
  useLoadBi('plans-list', { isLoading: eligible === null, isError: eligible === 'error' });

  // Credits/packages picker viewed (mirrors native's af_content_view). Once per mount. Both
  // destinations carry the origin, so the acquisition funnels split by entry point the same way the
  // src:172 view above does.
  useEffect(() => {
    logPackagePickerView(attribution.origin);
    pushGtmEvent(GtmEvent.packagePickerView, attribution.origin ? { origin: attribution.origin } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On return from Checkout (?status=success&session_id=…), the redirect alone
  // does NOT prove payment — async/incomplete sessions land here too. Retrieve
  // the session and only claim success (SymphonyPricing then polls the premium
  // for the webhook-driven grant) when it actually completed + paid.
  useEffect(() => {
    // An Elements buyer coming back from a bank redirect carries the intent's client secret, not a
    // session id. Read BOTH the URL and the stash, and read them BEFORE the status check below.
    //
    // Before the stash existed there was nothing to recover; now there is, and the status check
    // would have thrown it away. `stripCheckoutParams` clears the query string as soon as we have
    // captured it, so on the very refresh this recovery exists for there is no `?status=success`
    // left — the effect would take the branch below, clear the outcome, re-enable Buy, and leave a
    // paid-for purchase looking like nothing had happened.
    const urlElementsReturn = readElementsReturn(
      typeof window === 'undefined' ? '' : window.location.search,
    );
    const stashedElementsReturn = readStashedElementsReturn();
    // The URL is authoritative for the intent handle; the pre-confirm stash is the only place the
    // finalized amount survives the bank navigation. Merge both into the once-only settlement row.
    const elementsReturn: ElementsReturn | null = urlElementsReturn
      ? { ...(stashedElementsReturn ?? {}), ...urlElementsReturn }
      : stashedElementsReturn;

    if (status !== 'success' && !elementsReturn) {
      setPaymentOutcome(null);
      setOptimisticPurchase(null);
      clearPendingPurchase();
      if (status === 'cancelled') stripCheckoutParams();
      return;
    }
    const sessionId = typeof router.query.session_id === 'string' ? router.query.session_id : undefined;
    const billingLane = typeof router.query.billing_lane === 'string' ? router.query.billing_lane : undefined;
    // Persist BEFORE stripping the URL / starting the verify fetch: if this tab is closed or
    // reloaded before verification resolves, the session id is otherwise gone for good and the
    // purchase never gets reported. The resume effect below is what actually uses this.
    if (sessionId) writePendingVerify(sessionId, billingLane);
    // Strip ?status/?session_id from the address bar now that we've captured what we need. Uses
    // history.replaceState (not router.replace) so Next's router.query is untouched — the outcome
    // still flows through paymentOutcome (React state) this session, but a manual refresh loads a
    // clean URL and can't replay the "payment received" toast.
    // Settle the Elements return against Stripe directly — the session verifier below cannot read a
    // client secret, and its `incomplete` fallback would report a successful payment as a failure.
    if (elementsReturn) {
      stashElementsReturn(elementsReturn);
    }
    stripCheckoutParams();
    if (elementsReturn) {
      let cancelledReturn = false;
      setPaymentOutcome('verifying');
      (async () => {
        // Retried, because the FIRST read failing is the whole problem: one transient miss used to
        // leave the buyer on an ordinary picker with the only handle on their payment already
        // stripped from the URL.
        for (let attempt = 0; attempt < RETURN_VERIFY_ATTEMPTS && !cancelledReturn; attempt += 1) {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, RETURN_VERIFY_RETRY_MS));
            if (cancelledReturn) return;
          }
          const key = await fetch(`${API_BASE}/api/stripe/config`)
            .then((r) => (r.ok ? (r.json() as Promise<{ publishableKey?: string }>) : null))
            .then((body) => body?.publishableKey ?? '')
            .catch(() => '');
          if (cancelledReturn) return;
          if (!key) {
            continue;
          }
          const stripe = await getStripe(key).catch(() => null);
          if (cancelledReturn) return;
          if (!stripe) {
            continue;
          }
          const verdict = await resolveElementsReturn(stripe, elementsReturn);
          if (cancelledReturn) return;
          if (verdict === 'unsettled') {
            continue;
          }
          // Definitive, either way — the handle has done its job and must not outlive it, or the
          // next visit re-settles a purchase that is already resolved.
          clearStashedElementsReturn();
          if (verdict === 'paid' || verdict === 'saved') {
            // Same distinction on the way back from a redirect: a stored card is not a payment.
            setPaymentOutcome(verdict === 'saved' ? 'started' : 'paid');
            if (verdict === 'paid') {
              // Once-only by construction: the stash clear above is this lane's latch.
              const purchase = readPurchaseAttribution();
              const reported = reportPurchaseSuccess(elementsReturn.amountMinor, elementsReturn.currency, purchase.origin);
              logPurchaseVerifiedRef.current(purchase, reported?.amount ?? null, reported?.currency ?? elementsReturn.currency ?? null);
              clearPurchaseAttribution();
            }
            try {
              window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
            } catch {
              /* no window — nothing to nudge */
            }
          } else {
            setPaymentOutcome('incomplete');
          }
          return;
        }
        // Out of attempts with nothing definitive. The money may well have moved, so this must NOT
        // read as a failure — the stash is KEPT, the picker stays in its settling state, and the
        // premium poll is what resolves it.
        if (!cancelledReturn) {
          setPaymentOutcome('verifying');
        }
      })();
      return () => {
        cancelledReturn = true;
      };
    }
    if (!sessionId) {
      setPaymentOutcome('incomplete');
      setOptimisticPurchase(null);
      clearPendingPurchase();
      return;
    }
    let cancelled = false;
    setPaymentOutcome('verifying');
    (async () => {
      const result = await verifyCheckoutSession(sessionId, billingLane, (purchase, amount, currency) =>
        logPurchaseVerifiedRef.current(purchase, amount, currency),
      );
      if (cancelled) return;
      const paid = result.outcome === 'paid';
      // Purchase success — BOTH destinations carry the transaction value + currency (web-only:
      // native can't read a Stripe amount). AppsFlyer's own af_revenue/af_currency is sent inside
      // verifyCheckoutSession (via logPurchaseSuccessOnce); GTM uses the canonical value/currency
      // its purchase tag reads for revenue reporting and value-based bidding. A 100%-off /
      // no_payment_required purchase still has amount 0 + currency, so it carries value:0 — only a
      // genuinely absent amount falls back to the bare event.
      if (paid) {
        pushGtmEvent(
          GtmEvent.purchaseSuccess,
          result.amount !== null && result.currency ? { value: result.amount, currency: result.currency } : {},
        );
      }
      // An 'unresolved' outcome (retries exhausted) reads as incomplete for THIS load, same as a
      // confirmed non-payment — but unlike a confirmed non-payment, the pending-verify record was
      // NOT cleared, so the resume effect below still reports the purchase on a later visit even
      // though this tab won't see it.
      setPaymentOutcome(paid ? 'paid' : 'incomplete');
      // Only seed the optimistic subscribed view once Stripe confirms the payment; discard the
      // stash either way so a later manual refresh (no fresh outcome) can't re-apply it.
      setOptimisticPurchase(paid ? readPendingPurchase() : null);
      clearPendingPurchase();
      // Nudge the always-mounted sidebar/settings credit readout to poll for the webhook grant so
      // it updates on its own, rather than showing the pre-purchase balance until a manual refresh.
      if (paid) {
        try {
          window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
        } catch {
          /* SSR / no window — the picker poll still reconciles the picker's own view */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, router.query.session_id, router.query.billing_lane]);

  // Resume a verification a PRIOR visit couldn't finish — this tab was closed mid-verify, or
  // verifyCheckoutSession above exhausted its retries. Runs silently: by the time this fires the
  // purchase may be minutes old, so it reports the analytics event and nudges the credits poll but
  // does not replay the "payment received" screen (paymentOutcome/optimisticPurchase are untouched).
  useEffect(() => {
    if (status === 'success') return; // the effect above already owns this load's session id
    const pending = readPendingVerify();
    if (!pending) return;
    let cancelled = false;
    (async () => {
      const result = await verifyCheckoutSession(pending.sessionId, pending.billingLane, (purchase, amount, currency) =>
        logPurchaseVerifiedRef.current(purchase, amount, currency),
      );
      if (cancelled || result.outcome !== 'paid') return;
      try {
        window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
      } catch {
        /* SSR / no window */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Region/employee gate: ask the server whether this visitor may use the picker. A non-OK or
  // non-true answer → blocked (fail-closed), which renders the "not available in your region" state.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/eligibility`, { credentials: 'include' })
      .then(async (r) => {
        if (cancelled) return;
        // Only a 200 is an authoritative allow/deny (the route fails closed to {allowed:false} on a
        // server-side error). A non-200 (e.g. 503 no_bootstrap_context) is an infra hiccup → 'error',
        // not a region block.
        if (!r.ok) {
          setEligible('error');
          return;
        }
        const d = (await r.json()) as { allowed?: boolean };
        setEligible(d?.allowed === true);
      })
      .catch(() => {
        if (!cancelled) setEligible('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Publishable key for embedded Checkout (non-secret; fetched once).
  useEffect(() => {
    fetch(`${API_BASE}/api/stripe/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.publishableKey && setPublishableKey(d.publishableKey))
      .catch(() => {});
  }, []);

  // Mount embedded Checkout once we have both a session client secret and the
  // publishable key. Tears down on close/unmount (Stripe allows one instance).
  useEffect(() => {
    if (!embeddedSecret || !publishableKey) return;
    let destroyed = false;
    let instance: EmbeddedCheckout | null = null;
    (async () => {
      try {
        const stripe = await getStripe(publishableKey);
        const checkout = await stripe.initEmbeddedCheckout({ clientSecret: embeddedSecret });
        if (destroyed) {
          checkout.destroy();
          return;
        }
        instance = checkout;
        checkoutRef.current = checkout;
        if (mountRef.current) checkout.mount(mountRef.current);
        setCheckoutReady(true);
      } catch {
        setError(t('packages.error.loadForm'));
        setEmbeddedSecret(null);
      }
    })();
    return () => {
      destroyed = true;
      try {
        (instance ?? checkoutRef.current)?.destroy();
      } catch {
        /* already torn down */
      }
      checkoutRef.current = null;
      setCheckoutReady(false);
    };
  }, [embeddedSecret, publishableKey]);

  // Release the CTA's busy state the moment the payment form is actually usable — that is the
  // first point the buyer has something to look at, so it is the right moment to stop saying
  // "opening".
  useEffect(() => {
    if (checkoutReady) setPendingId(null);
  }, [checkoutReady]);

  // Safety net: never strand the CTA in its busy state. If the form has not mounted within this
  // window something went wrong (Stripe's script blocked, iframe refused to load), and the buyer
  // must be able to try again rather than stare at a permanently "opening" button. The modal's own
  // loading copy stays on screen, so they can also just close it.
  useEffect(() => {
    if (!pendingId || checkoutReady) return undefined;
    const id = window.setTimeout(() => setPendingId(null), CHECKOUT_BUSY_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [pendingId, checkoutReady]);

  // Esc closes the modal (backdrop click already does). Stripe's iframe manages
  // its own internal focus, so we don't add a full focus trap.
  useEffect(() => {
    if (!embeddedSecret) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCheckout();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embeddedSecret]);

  // POST to checkout, open the embedded modal with the returned client secret.
  // The mount prefix rides along so the server returns the user to the picker on
  // the SAME mount they bought from (/orion-web, /symphony-web, or the root). The
  // root mount is the empty string — sent as-is so the server builds a bare-root
  // return URL rather than defaulting to /orion-web.
  const startCheckout = useCallback(
    async (
      body: Record<string, unknown>,
      pendingKey: string,
      reuseKey: string = pendingKey,
      // When true, `body.couponCode` was added by US (the auto personal-sale coupon), not typed by the
      // buyer — so it must NEVER block the purchase. On ANY checkout failure it is dropped and the
      // session retried at full price. A buyer's OWN coupon is not optional: its rejection surfaces.
      couponOptional = false,
    ) => {
      // Synchronous — survives React's batching where `pendingId` does not.
      if (checkoutInFlightRef.current) return;
      // Same target, clicked again within the reuse window, and we still hold its client secret:
      // re-open that session rather than creating a second one.
      //
      // `pendingKey` and `reuseKey` are deliberately different. The first identifies the CARD to put
      // in its busy state, so it stays the plain plan id; the second identifies the SESSION, and must
      // carry everything the session was minted with — a session is specific to a cadence and a promo
      // code, and matching on the plan alone re-opened a yearly session for a buyer who had gone back
      // and chosen monthly.
      const cached = lastCheckoutRef.current;
      if (cached && cached.key === reuseKey && Date.now() - cached.at < CHECKOUT_REUSE_MS) {
        setError(null);
        if (cached.publishableKey) setPublishableKey(cached.publishableKey);
        setEmbeddedSecret(cached.clientSecret);
        return;
      }
      checkoutInFlightRef.current = true;
      let opened = false;
      // Checkout START: keep what sent the buyer for the Stripe return, which is a fresh page load.
      stashPurchaseAttribution({ origin: attribution.origin, feature: attribution.feature });
      setError(null);
      setPendingId(pendingKey);
      // Purchase page view / checkout opened (mirrors native's af_initiated_checkout).
      logPurchasePageView();
      pushGtmEvent(GtmEvent.purchasePageView);
      // The GA4 session this purchase happens in, so the server's `symphony_premium` gets a source.
      registerGa4CheckoutSession();
      try {
        const mount = basePath.replace(/^\//, '');
        const post = async (attemptBody: Record<string, unknown>) => {
          const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            // `origin` / `feature` ride along for the server's `[checkout-origin]` log line — the join key
            // to Stripe's export. Validated again server-side; these are only ever allowlisted values here.
            body: JSON.stringify({ ...attemptBody, mount, origin: attribution.origin, feature: attribution.feature }),
          });
          const json = (await res.json()) as {
            clientSecret?: string;
            publishableKey?: string;
            error?: string;
            message?: string;
          };
          return { res, json };
        };
        let { res, json } = await post(body);
        // The auto coupon must NEVER prevent a purchase. On ANY failed attempt that carried it — a
        // sandbox lane that refuses coupons, a validate/checkout race, a catalog misconfig, anything —
        // drop it and retry the session at full price rather than surfacing a rejection. If the retry
        // also fails, that is a real (non-coupon) problem and its error is surfaced below. A buyer's
        // OWN coupon is never `couponOptional`, so its rejection still reaches them unchanged.
        let droppedCoupon = false;
        if (!res.ok && couponOptional && body.couponCode) {
          ({ res, json } = await post({ ...body, couponCode: undefined }));
          droppedCoupon = true;
        }
        if (!res.ok || !json.clientSecret) {
          // `topup_not_allowed` is the server's plan↔pack enforcement (lib/topup-eligibility) — a
          // bare code, so localize it here rather than showing the raw string. Everything else keeps
          // the service's own message, which usually names the real problem (e.g. a rejected coupon).
          setError(
            json.error === 'topup_not_allowed'
              ? t('packages.error.topupNotAllowed')
              : json.message || json.error || t('packages.error.startCheckout'),
          );
          return;
        }
        // Remember the plan being bought so the return page (a fresh navigation that resets this
        // state) can optimistically flip to the subscribed view once Stripe confirms payment. Only
        // the Symphony (agentPlanId) path — the legacy packageId path has no picker "current plan".
        if (typeof body.agentPlanId === 'string') {
          try {
            sessionStorage.setItem(
              PENDING_PURCHASE_KEY,
              JSON.stringify({ agentPlanId: body.agentPlanId, cadence: (body.cadence as Cadence) ?? 'monthly' }),
            );
          } catch {
            /* sessionStorage unavailable — the optimistic flip just won't apply; poll still catches up */
          }
        }
        // The response key is tied to the same server-resolved metasite mode as the Checkout
        // Session. This replaces a possibly stale page-load key after switching picker sites.
        if (json.publishableKey) setPublishableKey(json.publishableKey);
        // Don't reuse-cache a session whose auto coupon we DROPPED: `reuseKey` still names the coupon,
        // so caching the full-price session under it would reopen full price on a re-click within the
        // window — and stick a merely-transient coupon failure at full price. Skipping the cache lets
        // the next click re-attempt the coupon fresh. The modal still opens for this purchase.
        if (!droppedCoupon) {
          lastCheckoutRef.current = {
            key: reuseKey,
            at: Date.now(),
            clientSecret: json.clientSecret,
            publishableKey: json.publishableKey ?? null,
          };
        }
        setEmbeddedSecret(json.clientSecret); // opens the modal; the effect mounts it
        opened = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : t('packages.error.startCheckout'));
      } finally {
        checkoutInFlightRef.current = false;
        // Only drop the button's busy state here when NOTHING opened. On the success path the
        // POST returning is not the moment the buyer sees anything: Stripe's iframe still has to
        // load and mount, and clearing `pendingId` now returns the CTA to its idle label while the
        // screen is still unchanged. That gap is what produced eight sessions from one buyer in
        // three minutes on 2026-08-20. The busy state is released when the form is actually up
        // (or by the safety timeout below), not when the network call finishes.
        if (!opened) setPendingId(null);
      }
    },
    [basePath, t, attribution],
  );

  // Check out an AgentPlan option at a cadence (price resolved server-side). `couponCode` is the
  // code the buyer applied on that card — already validated against this exact (plan, cadence)
  // target, but re-validated server-side at CreateCheckoutSession, which is the only place the
  // discount is actually applied. An invalid code there fails the checkout with no Stripe session.
  const buyPlan = useCallback(
    async (agentPlanId: string, cadence: Cadence, planKey: string, couponCode?: string) => {
      // AUTO personal-sale coupon. When the buyer is personal-sale eligible on the YEARLY cadence, on a
      // plan the offer COVERS (Pro/Max — not Basic), and applied NO coupon of their own, carry
      // PERSONAL_SALE_COUPON — but PRE-VALIDATE it first and drop it (full price) on any rejection, so an
      // eligible buyer is never blocked by it. The buyer's own coupon always wins (it is already
      // `couponCode` here). This runs BEFORE the lane split, so both lanes carry a code that is already
      // known-good — or none. `shouldAttemptPersonalSaleCoupon` keeps it off the sandbox lane, which
      // refuses coupons.
      //
      // `planKey` is the CARD key (`basic` | `pro` | `max`), not the AgentPlan id: the id is a per-market
      // GUID, so gating on it here would need the catalog this callback does not hold — the picker knows
      // the tier and hands it down, which is also the value its own 30%-OFF visual is gated on.
      let effectiveCoupon = couponCode;
      if (shouldAttemptPersonalSaleCoupon({ userCoupon: couponCode, personalSaleActive, cadence, planKey, liveLane: !sandbox })) {
        effectiveCoupon = (await validateCouponCode(PERSONAL_SALE_COUPON, agentPlanId, cadence))
          ? PERSONAL_SALE_COUPON
          : undefined;
      }
      // A coupon WE added (no coupon of the buyer's own) is optional at checkout — it must never block
      // the purchase, so `startCheckout` drops it and retries at full price on any failure.
      const couponOptional = !couponCode && Boolean(effectiveCoupon);
      // The `buy_plan` 198:10 is NOT fired here any more. This callback knows only an AgentPlan id,
      // which is what put a raw GUID in `component_name` and left `component_route` with no cadence
      // in it. Both facts live one level down, in the card and the billing-cycle step, so the event
      // is fired there — see `logBuyPlan` in SymphonyPricing.
      // The reuse key is the whole TARGET, not just the plan.
      //
      // `startCheckout` re-opens a cached Stripe session whenever the key matches inside
      // CHECKOUT_REUSE_MS, and `cadence` and `couponCode` travel in the body where the key could not
      // see them. So "buy, look at the price, go back, pick the other cycle, buy again" re-opened the
      // FIRST session: the buyer chose monthly and Stripe collected a year, silently, for up to 12x
      // the intended amount. The same shape dropped a promo code applied on the second pass.
      //
      // Reachable before the billing-cycle step existed (the cadence toggle could do it), but that
      // step makes going back and changing the cycle a designed part of the flow, with a Back button
      // built for it — so what was a narrow race becomes the ordinary path.
      // Our own checkout screen, when this lane is on it. Everything below — the session reuse, the
      // Stripe-return round trip, the optimistic stash — belongs to hosted Checkout and is left
      // untouched, so turning the toggle off restores exactly today's behaviour.
      if (elementsCheckout) {
        setError(null);
        // A plan is always chosen ON the picker, so Back from checkout returns here.
        void router.push(checkoutHref(basePath, { agentPlanId, cadence, couponCode: effectiveCoupon }, {
          origin: attribution.origin,
          feature: attribution.feature,
          back: 'picker',
        }));
        return undefined;
      }
      return startCheckout(
        { agentPlanId, cadence, couponCode: effectiveCoupon },
        agentPlanId,
        `${agentPlanId}:${cadence}:${effectiveCoupon ?? ''}`,
        couponOptional,
      );
    },
    // logPageClickBi is a fresh ref each render; excluded so the callback identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startCheckout, elementsCheckout, router, basePath, attribution.origin, attribution.feature, personalSaleActive, sandbox],
  );

  // Check out a one-time top-up pack (price + credit grant resolved server-side from the
  // AgentPack catalog). No sessionStorage stash: unlike a plan purchase there's no card to flip
  // optimistically — the return page's generic paid handling (success toast + the
  // CREDITS_REFRESH_EVENT nudge to the credit readouts) covers everything a top-up changes.
  const buyTopup = useCallback(
    (packId: string) => {
      logPageClickBi({ page_name: 'packages', button_name: 'buy_topup' }, { componentName: packId });
      if (elementsCheckout) {
        setError(null);
        // A DEEPLINKED top-up opened checkout the moment this page loaded, so the buyer never saw
        // the picker and Back must return to the surface that sent them. Any other top-up was
        // chosen here.
        void router.push(checkoutHref(basePath, { packId, cadence: 'monthly' }, {
          origin: attribution.origin,
          feature: attribution.feature,
          back: packId === autoTopupPackId ? 'origin' : 'picker',
        }));
        return undefined;
      }
      return startCheckout({ packId }, packId);
    },
    // logPageClickBi is a fresh ref each render; excluded so the callback identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startCheckout, elementsCheckout, router, basePath, attribution.origin, attribution.feature, autoTopupPackId],
  );

  const closeCheckout = () => {
    logPageClickBi({ page_name: 'packages', button_name: 'close_checkout' });
    try {
      checkoutRef.current?.destroy();
    } catch {
      /* already torn down */
    }
    checkoutRef.current = null;
    setEmbeddedSecret(null);
    setCheckoutReady(false);
    setPendingId(null);
  };

  // While the embedded checkout is up, this page IS a payment form, and the personal-sale band has to
  // come off it exactly as it does on `/checkout` — the band advertises the offer over the price the
  // buyer is being asked to pay, and on a phone it eats the top of the form. The URL stays `/packages`
  // here, so `AppFrame`'s route gate cannot see this; the claim is how it finds out.
  //
  // Keyed on `embeddedSecret`, the same value the modal itself renders from, so the band comes back the
  // instant the modal closes (or fails to open) with no second piece of state to keep in step.
  useSuppressPersonalSale(embeddedSecret !== null);
  useAffiliateLandingRedirect(paymentOutcome === 'paid');

  // The embedded-checkout modal.
  const checkoutModal = embeddedSecret ? (
    <ScrollArea
      role="dialog"
      aria-modal="true"
      onClick={closeCheckout}
      className="orion-pane-modal"
      style={CHECKOUT_SCRIM}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 540,
          background: 'var(--ds-surface-sunken)',
          borderRadius: 14,
          padding: 14,
          boxShadow: '0 24px 60px -20px rgba(var(--ds-black-rgb), 0.6)',
        }}
      >
        <button
          type="button"
          onClick={closeCheckout}
          aria-label={t('common.close')}
          style={{
            position: 'absolute',
            top: 6,
            insetInlineEnd: 12,
            zIndex: 1,
            background: 'transparent',
            border: 'none',
            fontSize: 24,
            lineHeight: 1,
            color: 'var(--ds-control-fill-off-hover)',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        {!checkoutReady && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 160,
              color: 'var(--ds-text-placeholder)',
              fontSize: 13,
            }}
          >
            {t('packages.loadingPayment')}
          </div>
        )}
        <div ref={mountRef} />
      </div>
    </ScrollArea>
  ) : null;

  // The MKT pricing design on white — rendered on every mount. 24px bottom padding sets the gap
  // below the bottom-pinned disclaimer footer (SymphonyPricing pins it with margin-top: auto); the
  // full-width footer is centered, so it clears the bottom-right FloatingChat composer bubble.
  return (
    <GenericEventsRouteProvider value="packages">
    <ScrollArea
      component="section"
      className="orion-content-pane"
      style={{ position: 'absolute', top: 0, bottom: 0, zIndex: 4, padding: '72px 40px 24px', background: 'var(--ds-surface-standard-bg)' }}
    >
      <div style={{ maxWidth: 1717, margin: '0 auto', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* DESKTOP back. The header chevron is mobile-only, so on wide a drill-in's way out lives in
            the page content — and the picker is a drill-in: Settings, the sidebar and the assistant's
            credit wall all push it. Pops history, so it returns to whatever opened it; renders
            nothing on narrow, where the chevron covers it. */}
        <div style={{ alignSelf: 'flex-start' }}>
          <DesktopBack />
        </div>
        {eligible === false ? (
          // Not available in this region (and not a Wix employee) — the Symphony (Stripe) picker is
          // US-only, so instead of a dead-end message embed the legacy Wix ("Wixel") pricing page so
          // the visitor can still pick a package in place. The server still refuses this account's
          // plans/checkout/switch calls independently; those aren't the Wixel purchase path.
          <iframe
            src={WIXEL_PRICING_URL}
            title={t('packages.wixelFrameTitle')}
            style={{ flexGrow: 1, width: '100%', minHeight: '80vh', border: 0, borderRadius: 14, background: 'var(--ds-surface-sunken)' }}
          />
        ) : eligible === 'error' ? (
          // The eligibility probe failed (infra/network) — NOT a region block. Offer a retry rather
          // than the misleading region copy (an eligible US user could land here on a transient blip).
          <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 440, color: 'var(--ds-text-secondary)' }}>
            <p style={{ fontSize: 14, lineHeight: 1.5 }}>{t('packages.error.availability')}</p>
            <button
              type="button"
              onClick={() => {
                logPageClickBi({ page_name: 'packages', button_name: 'retry' });
                router.reload();
              }}
              style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--ds-separator-default)',
                background: 'var(--ds-surface-sunken)', color: 'var(--ds-surface-sunken-dark)', fontSize: 13, cursor: 'pointer' }}
            >
              {t('common.retry')}
            </button>
          </div>
        ) : eligible === null ? (
          // Still resolving eligibility — don't flash the plans before we know.
          <div style={{ margin: 'auto', color: 'var(--ds-text-placeholder)', fontSize: 13 }}>{t('packages.loadingPayment')}</div>
        ) : (
          <PurchaseAttributionContext.Provider value={attribution}>
          <SymphonyPricing
            onCheckout={buyPlan}
            onTopup={buyTopup}
            pendingId={pendingId}
            // A settling return blocks buying too: the buyer may already have paid, and a second
            // purchase from this screen would charge them again for it.
            disabled={pendingId !== null || paymentOutcome === 'verifying'}
            status={
              paymentOutcome === 'paid'
                ? 'success'
                : paymentOutcome === 'started'
                  ? 'started'
                  : paymentOutcome === 'verifying'
                    ? 'settling'
                    : null
            }
            optimisticPurchase={paymentOutcome === 'paid' ? optimisticPurchase : null}
            error={error}
            autoTopupPackId={autoTopupPackId}
          />
          </PurchaseAttributionContext.Provider>
        )}
      </div>
      {checkoutModal}
    </ScrollArea>
    </GenericEventsRouteProvider>
  );
}
